<template>
  <div class="flex flex-col gap-6">

    <!-- Header -->
    <AppPageHeader :title="t('workingHours.title')" :description="t('workingHours.description')">
      <template #actions>
        <div class="flex items-center gap-2 text-xs text-muted-foreground mr-2">
          <Clock class="h-3.5 w-3.5" />
          <span>{{ lastUpdated ? t('workingHours.updated') + ' ' + lastUpdated : t('workingHours.neverSaved') }}</span>
        </div>
        <AppButton variant="outline" :disabled="loading" @click="loadConfiguration">
          <RefreshCw :class="['h-4 w-4 mr-2', loading ? 'animate-spin' : '']" />{{ t('workingHours.refresh') }}
        </AppButton>
        <AppButton :disabled="saving" @click="saveConfiguration">
          <Save class="h-4 w-4 mr-2" />{{ saving ? t('workingHours.saving') : t('workingHours.saveChanges') }}
        </AppButton>
      </template>
    </AppPageHeader>

    <!-- Main Panel -->
    <AppCard>
      <!-- Tab Navigation -->
      <div class="flex overflow-x-auto border-b border-border">
        <button v-for="tab in tabs" :key="tab.key" @click="activeTab = tab.key"
          :class="['flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors', activeTab === tab.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted']">
          <component :is="tab.icon" class="h-4 w-4" />
          {{ tab.name }}
        </button>
      </div>

      <!-- Tab Content -->
      <div class="p-4 md:p-6">

        <!-- Global Settings Tab -->
        <div v-show="activeTab === 'global'" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Timezone -->
          <div class="p-5 rounded-xl border border-border">
            <h4 class="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Globe class="h-4 w-4 text-primary" />{{ t('workingHours.systemSettings') }}
            </h4>
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-foreground">{{ t('workingHours.timezone') }}</label>
              <select v-model="config.timezone" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
                <option value="Europe/London">London</option>
                <option value="Asia/Tokyo">Tokyo</option>
              </select>
            </div>
          </div>

          <!-- Auto Clock-out -->
          <div class="p-5 rounded-xl border border-border">
            <h4 class="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Timer class="h-4 w-4 text-green-500" />{{ t('workingHours.autoClockOut') }}
            </h4>
            <div class="flex flex-col gap-4">
              <label class="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" v-model="config.autoClockOut.enabled" class="rounded" />
                <div>
                  <p class="text-sm font-medium text-foreground">{{ t('workingHours.enableAutoClockOut') }}</p>
                  <p class="text-xs text-muted-foreground">{{ t('workingHours.enableAutoClockOutDesc') }}</p>
                </div>
              </label>
              <div v-if="config.autoClockOut.enabled" class="flex flex-col gap-1">
                <label class="text-sm font-medium text-foreground">{{ t('workingHours.gracePeriod') }}</label>
                <input type="number" v-model.number="config.autoClockOut.gracePeriod" min="0" max="60" placeholder="15" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
                <p class="text-xs text-muted-foreground">{{ t('workingHours.gracePeriodDesc') }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Weekly Schedule Tab -->
        <div v-show="activeTab === 'schedule'" class="flex flex-col gap-4">
          <div class="flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20 text-sm text-primary">
            <Info class="h-4 w-4 shrink-0 mt-0.5" />
            <span>{{ t('workingHours.scheduleNote') }}</span>
          </div>
          <div class="flex flex-col gap-3">
            <div v-for="day in daysOfWeek" :key="day.key" class="p-4 rounded-xl border border-border">
              <div class="flex items-center justify-between mb-3">
                <div class="flex items-center gap-3">
                  <button @click="config.workingDays[day.key].enabled = !config.workingDays[day.key].enabled"
                    :class="['px-3 py-1 rounded-md text-xs font-medium transition-colors', config.workingDays[day.key].enabled ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-muted text-muted-foreground']">
                    {{ config.workingDays[day.key].enabled ? t('workingHours.workDay') : t('workingHours.workDay') }}
                  </button>
                  <span class="text-sm font-semibold text-foreground">{{ day.name }}</span>
                </div>
                <span v-if="config.workingDays[day.key].enabled" class="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                  {{ safeCalculateWorkingHours(config.workingDays[day.key], day.key) }}
                </span>
              </div>
              <div v-if="config.workingDays[day.key].enabled" class="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-muted-foreground">{{ t('workingHours.startTime') }}</label>
                  <input type="time" v-model="config.workingDays[day.key].startTime" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-muted-foreground">{{ t('workingHours.endTime') }}</label>
                  <input type="time" v-model="config.workingDays[day.key].endTime" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-muted-foreground">{{ t('workingHours.breakDuration') }}</label>
                  <input type="number" v-model="config.workingDays[day.key].breakDuration" min="0" max="120" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
                </div>
              </div>
              <div v-else class="flex items-center justify-center py-4 gap-2 text-muted-foreground">
                <Moon class="h-4 w-4" /><span class="text-sm">{{ t('workingHours.weeklySchedule') }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Overtime Policies Tab -->
        <div v-show="activeTab === 'overtime'" class="flex flex-col gap-6">
          <div class="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-300">
            <AlertCircle class="h-4 w-4 shrink-0 mt-0.5" />
            <span>{{ t('workingHours.overtimeNote') }}</span>
          </div>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div class="p-5 rounded-xl border border-border flex flex-col gap-4">
              <h4 class="text-sm font-semibold text-foreground flex items-center gap-2"><Clock class="h-4 w-4 text-orange-500" />{{ t('workingHours.overtime') }}</h4>
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-foreground">{{ t('workingHours.maxOvertimeHours') }}</label>
                <input type="number" v-model.number="config.overtime.maxDailyHours" min="0" max="12" step="0.5" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-foreground">{{ t('workingHours.minOvertimeMinutes') }}</label>
                <input type="number" v-model.number="config.overtime.maxWeeklyHours" min="0" max="40" step="0.5" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
              </div>
            </div>
            <div class="p-5 rounded-xl border border-border flex flex-col gap-4">
              <h4 class="text-sm font-semibold text-foreground flex items-center gap-2"><DollarSign class="h-4 w-4 text-green-500" />{{ t('workingHours.overtime') }}</h4>
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-foreground">{{ t('workingHours.overtimeRateMultiplier') }}</label>
                <select v-model="config.overtime.multiplier" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                  <option value="1.25">1.25× (Time and Quarter)</option>
                  <option value="1.5">1.5× (Time and Half)</option>
                  <option value="2.0">2.0× (Double Time)</option>
                </select>
              </div>
              <label class="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" v-model="config.overtime.requiresApproval" class="rounded" />
                <div>
                  <p class="text-sm font-medium text-foreground">{{ t('workingHours.requireApproval') }}</p>
                  <p class="text-xs text-muted-foreground">{{ t('workingHours.requireApproval') }}</p>
                </div>
              </label>
              <div v-if="!config.overtime.requiresApproval" class="flex flex-col gap-1">
                <label class="text-sm font-medium text-foreground">Auto-approve under (minutes)</label>
                <input type="number" v-model.number="config.overtime.autoApproveThreshold" min="0" max="120" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
                <p class="text-xs text-muted-foreground">Automatically approve overtime under this duration</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Department Overrides Tab -->
        <div v-show="activeTab === 'departments'" class="flex flex-col gap-4">
          <div class="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border border-border text-sm text-muted-foreground">
            <Building class="h-4 w-4 shrink-0 mt-0.5" />
            <span>Custom working hours per department override global settings.</span>
          </div>
          <AppEmptyState v-if="departments.length === 0" :icon="Building" title="No Departments Found" description="Create departments first to configure department-specific hours." />
          <div v-else class="flex flex-col gap-3">
            <div v-for="department in departments" :key="department.id" class="rounded-xl border border-border overflow-hidden">
              <div class="flex items-center justify-between px-4 py-3 bg-muted/30">
                <div class="flex items-center gap-3">
                  <div class="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center"><Building class="h-4 w-4 text-primary" /></div>
                  <div><p class="text-sm font-semibold text-foreground">{{ department.name }}</p><p class="text-xs text-muted-foreground">{{ department.description || 'No description' }}</p></div>
                </div>
                <AppButton size="sm" :variant="config.departmentOverrides[department.id] ? 'destructive' : 'outline'" @click="toggleDepartmentOverride(department.id)">
                  {{ config.departmentOverrides[department.id] ? 'Remove Override' : 'Add Override' }}
                </AppButton>
              </div>
              <div v-if="config.departmentOverrides[department.id]" class="p-4 border-t border-border">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-muted-foreground">Start Time</label>
                    <input type="time" v-model="config.departmentOverrides[department.id].startTime" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
                  </div>
                  <div class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-muted-foreground">End Time</label>
                    <input type="time" v-model="config.departmentOverrides[department.id].endTime" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
                  </div>
                  <div class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-muted-foreground">OT Multiplier</label>
                    <select v-model="config.departmentOverrides[department.id].overtimeMultiplier" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                      <option value="1.25">1.25×</option>
                      <option value="1.5">1.5×</option>
                      <option value="2.0">2.0×</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Notifications Tab -->
        <div v-show="activeTab === 'notifications'" class="flex flex-col gap-4">
          <div class="flex items-start gap-2 p-3 rounded-lg bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800 text-sm text-green-800 dark:text-green-300">
            <Bell class="h-4 w-4 shrink-0 mt-0.5" />
            <span>Configure notifications for employees and administrators about working hours events.</span>
          </div>
          <div class="flex flex-col gap-3">
            <div class="p-4 rounded-xl border border-border flex items-start gap-3">
              <input type="checkbox" v-model="config.notifications.autoClockOut" class="rounded mt-0.5" />
              <div>
                <p class="text-sm font-medium text-foreground flex items-center gap-2"><Timer class="h-4 w-4 text-primary" />Auto Clock-out Notifications</p>
                <p class="text-xs text-muted-foreground mt-1">Notify employees 15 minutes before automatic clock-out</p>
              </div>
            </div>
            <div class="p-4 rounded-xl border border-border flex items-start gap-3">
              <input type="checkbox" v-model="config.notifications.overtimeRequests" class="rounded mt-0.5" />
              <div>
                <p class="text-sm font-medium text-foreground flex items-center gap-2"><Clock class="h-4 w-4 text-orange-500" />Overtime Notifications</p>
                <p class="text-xs text-muted-foreground mt-1">Notify administrators when employees request overtime</p>
              </div>
            </div>
            <div class="p-4 rounded-xl border border-border flex items-start gap-3">
              <input type="checkbox" v-model="config.notifications.scheduleChanges" class="rounded mt-0.5" />
              <div>
                <p class="text-sm font-medium text-foreground flex items-center gap-2"><Calendar class="h-4 w-4 text-purple-500" />Schedule Change Notifications</p>
                <p class="text-xs text-muted-foreground mt-1">Notify employees when their working schedule is modified</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </AppCard>

  </div>
</template>
<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { 
  Save, Clock, RefreshCw, Users, Globe, Timer, Info, AlertCircle, 
  DollarSign, Building, Bell, Calendar, Moon 
} from 'lucide-vue-next';

// Import services
import attendanceService from '@/services/attendance';
import adminService from '@/services/adminService';
import { AppPageHeader, AppCard, AppButton, AppEmptyState } from '@/components/ui';

const { t, locale } = useI18n();

// Reactive data
const saving = ref(false);
const loading = ref(false);
const departments = ref([]);
const activeTab = ref('global');
const lastUpdated = ref(null);

// Tab configuration
const tabs = computed(() => [
  { key: 'global', name: t('workingHours.systemSettings'), icon: Globe },
  { key: 'schedule', name: t('workingHours.weeklySchedule'), icon: Calendar },
  { key: 'overtime', name: t('workingHours.overtime'), icon: Clock },
  { key: 'departments', name: t('departments.title'), icon: Building },
  { key: 'notifications', name: t('notifications.title'), icon: Bell }
]);

const daysOfWeek = computed(() => {
  const isAr = locale.value === 'ar';
  return [
    { key: 'monday',    name: isAr ? 'الاثنين'  : 'Monday'    },
    { key: 'tuesday',   name: isAr ? 'الثلاثاء' : 'Tuesday'   },
    { key: 'wednesday', name: isAr ? 'الأربعاء' : 'Wednesday' },
    { key: 'thursday',  name: isAr ? 'الخميس'   : 'Thursday'  },
    { key: 'friday',    name: isAr ? 'الجمعة'   : 'Friday'    },
    { key: 'saturday',  name: isAr ? 'السبت'    : 'Saturday'  },
    { key: 'sunday',    name: isAr ? 'الأحد'    : 'Sunday'    },
  ];
});


const config = ref({
  timezone: 'America/New_York',
  autoClockOut: {
    enabled: true,
    gracePeriod: 15
  },
  overtime: {
    requiresApproval: true,
    maxDailyHours: 4,
    maxWeeklyHours: 20,
    multiplier: 1.5,
    autoApproveThreshold: 30
  },
  workingDays: {
    sunday: { enabled: true, startTime: '08:00', endTime: '17:00', breakDuration: 60 },
    monday: { enabled: true, startTime: '08:00', endTime: '17:00', breakDuration: 60 },
    tuesday: { enabled: true, startTime: '08:00', endTime: '17:00', breakDuration: 60 },
    wednesday: { enabled: true, startTime: '08:00', endTime: '17:00', breakDuration: 60 },
    thursday: { enabled: true, startTime: '08:00', endTime: '17:00', breakDuration: 60 },
    friday: { enabled: false, startTime: '08:00', endTime: '17:00', breakDuration: 60 },
    saturday: { enabled: false, startTime: '08:00', endTime: '17:00', breakDuration: 60 }
  },
  departmentOverrides: {},
  notifications: {
    autoClockOut: true,
    overtimeRequests: true,
    scheduleChanges: true
  }
});

// Methods
const loadConfiguration = async () => {
  try {
    loading.value = true;
    console.log('🔄 Loading working hours configuration...');
    
    // Ensure we have fresh data from the database
    const response = await attendanceService.workingHours.admin.getWorkingHoursConfig();
    console.log('🐛 Raw API response:', response);
    console.log('🐛 Response type:', typeof response);
    console.log('🐛 Response keys:', Object.keys(response || {}));

    if (response?.data) {
      console.log('✅ Working hours config loaded:', response.data);
      
      // Validate and merge the data with proper structure
      const loadedData = response.data.data;
      console.log('🐛 Thursday data from API:', loadedData.workingDays?.thursday);
      
      // Ensure all required fields exist with defaults
      config.value = {
        timezone: loadedData.timezone || 'America/New_York',
        autoClockOut: {
          enabled: loadedData.autoClockOut?.enabled ?? true,
          gracePeriod: loadedData.autoClockOut?.gracePeriod ?? 15
        },
        overtime: {
          requiresApproval: loadedData.overtime?.requiresApproval ?? true,
          maxDailyHours: loadedData.overtime?.maxDailyHours ?? 4,
          maxWeeklyHours: loadedData.overtime?.maxWeeklyHours ?? 20,
          multiplier: loadedData.overtime?.multiplier ?? 1.5,
          autoApproveThreshold: loadedData.overtime?.autoApproveThreshold ?? 30
        },
        workingDays: {
          sunday: {
            enabled: loadedData.workingDays?.sunday?.enabled ?? true,
            startTime: ensureTimeFormat(loadedData.workingDays?.sunday?.startTime) ?? '08:00',
            endTime: ensureTimeFormat(loadedData.workingDays?.sunday?.endTime) ?? '17:00',
            breakDuration: loadedData.workingDays?.sunday?.breakDuration ?? 60
          },
          monday: {
            enabled: loadedData.workingDays?.monday?.enabled ?? true,
            startTime: ensureTimeFormat(loadedData.workingDays?.monday?.startTime) ?? '08:00',
            endTime: ensureTimeFormat(loadedData.workingDays?.monday?.endTime) ?? '17:00',
            breakDuration: loadedData.workingDays?.monday?.breakDuration ?? 60
          },
          tuesday: {
            enabled: loadedData.workingDays?.tuesday?.enabled ?? true,
            startTime: ensureTimeFormat(loadedData.workingDays?.tuesday?.startTime) ?? '08:00',
            endTime: ensureTimeFormat(loadedData.workingDays?.tuesday?.endTime) ?? '17:00',
            breakDuration: loadedData.workingDays?.tuesday?.breakDuration ?? 60
          },
          wednesday: {
            enabled: loadedData.workingDays?.wednesday?.enabled ?? true,
            startTime: ensureTimeFormat(loadedData.workingDays?.wednesday?.startTime) ?? '08:00',
            endTime: ensureTimeFormat(loadedData.workingDays?.wednesday?.endTime) ?? '17:00',
            breakDuration: loadedData.workingDays?.wednesday?.breakDuration ?? 60
          },
          thursday: {
            enabled: loadedData.workingDays?.thursday?.enabled ?? true,
            startTime: ensureTimeFormat(loadedData.workingDays?.thursday?.startTime) ?? '08:00',
            endTime: ensureTimeFormat(loadedData.workingDays?.thursday?.endTime) ?? '17:00',
            breakDuration: loadedData.workingDays?.thursday?.breakDuration ?? 60
          },
          friday: {
            enabled: loadedData.workingDays?.friday?.enabled ?? false,
            startTime: ensureTimeFormat(loadedData.workingDays?.friday?.startTime) ?? '08:00',
            endTime: ensureTimeFormat(loadedData.workingDays?.friday?.endTime) ?? '17:00',
            breakDuration: loadedData.workingDays?.friday?.breakDuration ?? 60
          },
          saturday: {
            enabled: loadedData.workingDays?.saturday?.enabled ?? false,
            startTime: ensureTimeFormat(loadedData.workingDays?.saturday?.startTime) ?? '08:00',
            endTime: ensureTimeFormat(loadedData.workingDays?.saturday?.endTime) ?? '17:00',
            breakDuration: loadedData.workingDays?.saturday?.breakDuration ?? 60
          }
        },
        departmentOverrides: loadedData.departmentOverrides || {},
        notifications: {
          autoClockOut: loadedData.notifications?.autoClockOut ?? true,
          overtimeRequests: loadedData.notifications?.overtimeRequests ?? true,
          scheduleChanges: loadedData.notifications?.scheduleChanges ?? true
        }
      };
      
      lastUpdated.value = new Date().toLocaleString();
      console.log('✅ Config validated and updated:', config.value);
      showToast('success', 'Working hours configuration loaded successfully');
    } else {
      console.warn('⚠️ No configuration data received, using defaults');
      showToast('warning', 'No saved configuration found. Using default settings.');
    }
  } catch (error) {
    console.error('❌ Error loading working hours configuration:', error);
    console.error('❌ Error details:', error.response || error);
    showToast('error', 'Failed to load configuration from database. Using default settings.');
    // Keep default config if API fails
  } finally {
    loading.value = false;
  }
};

const loadDepartments = async () => {
  try {
    console.log('🔄 Loading departments...');
    const departments_list = await adminService.getDepartments();
    departments.value = departments_list || [];
    console.log('✅ Departments loaded:', departments.value.length);
  } catch (error) {
    console.error('❌ Error loading departments:', error);
    departments.value = [];
  }
};

const saveConfiguration = async () => {
  try {
    saving.value = true;
    console.log('💾 Saving working hours configuration...');
    console.log('🐛 Data being sent to API:', JSON.stringify(config.value, null, 2));
    console.log('🐛 Thursday data being sent:', config.value.workingDays.thursday);

    await attendanceService.workingHours.admin.updateWorkingHoursConfig(config.value);
    lastUpdated.value = new Date().toLocaleString();

    console.log('✅ Working hours configuration saved successfully!');
    showToast('success', 'Working hours configuration saved successfully!');
  } catch (error) {
    console.error('❌ Error saving configuration:', error);
    showToast('error', 'Failed to save configuration. Please try again.');
  } finally {
    saving.value = false;
  }
};

// Enhanced toast notification helper
const showToast = (type, message) => {
  // Create a more user-friendly notification
  const toastContainer = document.getElementById('toast-container') || createToastContainer();
  
  const toast = document.createElement('div');
  const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500';
  const icon = type === 'success' ? '✅' : '❌';
  
  toast.className = `${bgColor} text-white px-6 py-4 rounded-lg shadow-lg mb-2 flex items-center gap-2 animate-slide-in`;
  toast.innerHTML = `
    <span class="text-lg">${icon}</span>
    <span>${message}</span>
  `;
  
  toastContainer.appendChild(toast);
  
  // Auto remove after 3 seconds
  setTimeout(() => {
    toast.remove();
  }, 3000);
};

const createToastContainer = () => {
  const container = document.createElement('div');
  container.id = 'toast-container';
  container.className = 'fixed top-4 right-4 z-50 max-w-sm';
  document.body.appendChild(container);
  return container;
};

// Helper function to ensure time format is HH:MM without timezone conversion
const ensureTimeFormat = (timeString) => {
  if (!timeString) return null;
  
  // If it's already in HH:MM format, return as-is
  if (typeof timeString === 'string' && /^\d{2}:\d{2}$/.test(timeString)) {
    return timeString;
  }
  
  // If it's in HH:MM:SS format, extract HH:MM
  if (typeof timeString === 'string' && /^\d{2}:\d{2}:\d{2}$/.test(timeString)) {
    return timeString.substring(0, 5);
  }
  
  // If it's a Date object or timestamp, convert to HH:MM in local time
  if (timeString instanceof Date || !isNaN(Date.parse(timeString))) {
    const date = new Date(timeString);
    // Use padStart to ensure 2-digit format
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }
  
  console.warn('Unknown time format:', timeString);
  return null;
};

const calculateWorkingHours = (dayConfig) => {
  if (!dayConfig.startTime || !dayConfig.endTime) return '';
  
  try {
    // Parse time strings as HH:MM format without timezone conversion
    const [startHours, startMinutes] = dayConfig.startTime.split(':').map(Number);
    const [endHours, endMinutes] = dayConfig.endTime.split(':').map(Number);
    
    // Convert to minutes for easier calculation
    const startTotalMinutes = startHours * 60 + startMinutes;
    let endTotalMinutes = endHours * 60 + endMinutes;
    
    // Handle cases where end time is next day (e.g., night shift)
    if (endTotalMinutes <= startTotalMinutes) {
      endTotalMinutes += 24 * 60; // Add 24 hours
    }
    
    // Calculate working minutes
    const totalMinutes = endTotalMinutes - startTotalMinutes;
    const breakMinutes = dayConfig.breakDuration || 0;
    const workingMinutes = totalMinutes - breakMinutes;
    
    // Convert to hours
    const workingHours = workingMinutes / 60;
    
    return `${workingHours.toFixed(1)}h working`;
  } catch (error) {
    console.error('🚨 Error calculating working hours:', error, dayConfig);
    return 'Invalid time';
  }
};

const safeCalculateWorkingHours = (dayConfig, dayName) => {
  try {
    console.log(`🐛 Safe calculating working hours for ${dayName}:`, dayConfig);
    const result = calculateWorkingHours(dayConfig);
    console.log(`🐛 Safe calculation result for ${dayName}:`, result);
    return result;
  } catch (error) {
    console.error(`🚨 Error calculating working hours for ${dayName}:`, error, dayConfig);
    return 'Error';
  }
};

const toggleDepartmentOverride = (departmentId) => {
  if (config.value.departmentOverrides[departmentId]) {
    delete config.value.departmentOverrides[departmentId];
  } else {
    config.value.departmentOverrides[departmentId] = {
      startTime: '08:00',
      endTime: '17:00',
      overtimeMultiplier: 1.5
    };
  }
};

// Handler for time input changes to debug timezone issues
const handleTimeInput = (dayKey, timeType, event) => {
  const inputValue = event.target.value;
  console.log(`🐛 Time input change - ${dayKey} ${timeType}:`, inputValue);
  console.log('🐛 Original value:', config.value.workingDays[dayKey][timeType]);
  
  // Set the value directly without any conversion
  config.value.workingDays[dayKey][timeType] = inputValue;
  
  console.log('🐛 New value set:', config.value.workingDays[dayKey][timeType]);
};

// Computed properties
const totalWorkingDays = computed(() => {
  return Object.values(config.value.workingDays).filter(day => day.enabled).length;
});

// Temporarily disable totalWeeklyHours to isolate the issue
const totalWeeklyHours = computed(() => {
  // Simple calculation without logging to prevent infinite loops
  const enabledDays = Object.values(config.value.workingDays).filter(day => day.enabled);
  return (enabledDays.length * 8.0).toFixed(1); // Simple fallback calculation
});

// Debug timezone and locale information
const debugTimezone = () => {
  console.log('🐛 Timezone Debug Info:', {
    userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),
    locale: navigator.language,
    currentTime: new Date().toISOString(),
    localTime: new Date().toLocaleString()
  });
};

// Lifecycle
onMounted(() => {
  debugTimezone();
  loadConfiguration();
  loadDepartments();
});
</script>

<style scoped>
.working-hours-config {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
}

@keyframes slide-in {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.animate-slide-in {
  animation: slide-in 0.3s ease-out;
}

/* Custom toggle switch styles */
.toggle-switch {
  @apply relative inline-block w-12 h-6;
}

.toggle-switch input {
  @apply sr-only;
}

.toggle-slider {
  @apply absolute cursor-pointer top-0 left-0 right-0 bottom-0 bg-gray-300 rounded-full transition-colors duration-300;
}

.toggle-slider:before {
  @apply absolute content-[''] h-4 w-4 left-1 top-1 bg-white rounded-full transition-transform duration-300;
}

input:checked + .toggle-slider {
  @apply bg-blue-600;
}

input:checked + .toggle-slider:before {
  @apply translate-x-6;
}

/* Tab transition effects */
.tab-content {
  @apply transition-all duration-300 ease-in-out;
}

/* Hover effects for cards */
.card-hover {
  @apply transition-all duration-200 ease-in-out;
}

.card-hover:hover {
  @apply shadow-md transform -translate-y-0.5;
}
</style>
